import { MapPin, Phone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import {
    getFooterMegaSections,
    getFooterSimpleLinks,
} from '../../data/footerNavigation';

function SocialIconFacebook({ size = 18 }) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden
        >
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
    );
}

function SocialIconX({ size = 18 }) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden
        >
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
    );
}

const FOOTER_PHONE_TEL = '+902122340403';
const CODIATECH_LOGO_SRC = '/assets/images/codiatech.svg';
const CODIATECH_HOME_URL = 'https://codiatech.com';

const SOCIAL_LINKS = [
    {
        id: 'facebook-page',
        Icon: SocialIconFacebook,
        href: 'https://www.facebook.com/ist.rtry',
        labelKey: 'footer.social.facebookPage',
    },
    {
        id: 'facebook-group',
        Icon: SocialIconFacebook,
        href: 'https://www.facebook.com/groups/391204477610636/?ref=ts&fref=ts',
        labelKey: 'footer.social.facebookGroup',
    },
    {
        id: 'twitter-club',
        Icon: SocialIconX,
        href: 'https://x.com/IstRotaryKulubu',
        labelKey: 'footer.social.twitterClub',
    },
    {
        id: 'twitter-service',
        Icon: SocialIconX,
        href: 'https://x.com/IstRotaryHizmet',
        labelKey: 'footer.social.twitterService',
    },
];

export function Footer() {
    const { t } = useTranslation();
    const megaSections = getFooterMegaSections();
    const simpleLinks = getFooterSimpleLinks();
    const addressLabel = t('footer.contactAddress');
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressLabel)}`;

    return (
        <footer className="site-footer mt-auto border-top">
            <div className="container py-5">
                <div className="row g-5 g-lg-4">
                    <div className="col-lg-4">
                        <Link
                            to="/"
                            className="footer-brand d-inline-flex align-items-center gap-2 text-decoration-none"
                        >
                            <img
                                src="/assets/images/rotary-logo.svg"
                                alt={t('header.rotaryAlt')}
                                className="footer-logo"
                            />
                        </Link>
                        <p className="site-footer-lead text-secondary small mt-3 mb-0">
                            {t('footer.lead')}
                        </p>
                        <ul className="list-unstyled site-footer-contact mt-4 mb-0 small">
                            <li className="d-flex gap-3 align-items-center mb-3">
                                <span className="footer-contact-icon-wrap d-inline-flex align-items-center justify-content-center flex-shrink-0">
                                    <Phone
                                        className="site-footer-contact-icon"
                                        size={18}
                                        strokeWidth={1.75}
                                        aria-hidden
                                    />
                                </span>
                                <a
                                    href={`tel:${FOOTER_PHONE_TEL}`}
                                    className="site-footer-link text-decoration-none"
                                >
                                    {t('footer.contactPhone')}
                                </a>
                            </li>
                            <li className="d-flex gap-3 align-items-start">
                                <span className="footer-contact-icon-wrap d-inline-flex align-items-center justify-content-center flex-shrink-0">
                                    <MapPin
                                        className="site-footer-contact-icon"
                                        size={18}
                                        strokeWidth={1.75}
                                        aria-hidden
                                    />
                                </span>
                                <a
                                    href={mapsUrl}
                                    className="site-footer-link text-decoration-none"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={t('footer.openAddressInMaps')}
                                >
                                    {addressLabel}
                                </a>
                            </li>
                        </ul>
                    </div>
                    <div className="col-lg-8">
                        <div className="row g-4">
                            {megaSections.map((section) => (
                                <div
                                    key={section.id}
                                    className="col-6 col-md-4 col-xl-3"
                                >
                                    <h3 className="footer-nav-heading h6">
                                        {t(section.titleKey)}
                                    </h3>
                                    <ul className="list-unstyled small mb-0">
                                        {section.links.map((item) => (
                                            <li
                                                key={`${section.id}-${item.to}`}
                                                className="mb-2"
                                            >
                                                <Link
                                                    to={item.to}
                                                    className="site-footer-link text-decoration-none"
                                                >
                                                    {t(item.linkKey)}
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                            <div className="col-6 col-md-4 col-xl-3">
                                <h3 className="footer-nav-heading h6">
                                    {t('footer.quickLinks')}
                                </h3>
                                <ul className="list-unstyled small mb-0">
                                    {simpleLinks.map((item) => (
                                        <li
                                            key={item.to}
                                            className="mb-2"
                                        >
                                            <Link
                                                to={item.to}
                                                className="site-footer-link text-decoration-none"
                                            >
                                                {t(item.linkKey)}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="site-footer-bar border-top">
                <div className="container py-3">
                    <div className="row align-items-center gy-3">
                        <div className="col-12 col-md-4 text-center text-md-start">
                            <p className="small text-secondary mb-0">
                                {t('footer.copyright', {
                                    year: new Date().getFullYear(),
                                })}
                            </p>
                        </div>
                        <div className="col-12 col-md-4 text-center">
                            <p className="site-footer-powered-inner d-flex align-items-center justify-content-center gap-2 mb-0 small text-secondary">
                                <span>{t('footer.poweredBy')}</span>
                                <a
                                    href={CODIATECH_HOME_URL}
                                    className="site-footer-powered-brand d-inline-flex align-items-center text-decoration-none"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={t('footer.poweredByLogoAlt')}
                                >
                                    <img
                                        src={CODIATECH_LOGO_SRC}
                                        alt=""
                                        className="site-footer-powered-logo"
                                        width={120}
                                        height={31}
                                        decoding="async"
                                    />
                                </a>
                            </p>
                        </div>
                        <div className="col-12 col-md-4 text-center text-md-end">
                            <div className="d-flex justify-content-center justify-content-md-end gap-2 flex-wrap">
                                {SOCIAL_LINKS.map(({ id, Icon, href, labelKey }) => (
                                    <a
                                        key={id}
                                        href={href}
                                        className="footer-social-link d-inline-flex align-items-center justify-content-center text-decoration-none"
                                        aria-label={t(labelKey)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <Icon size={18} />
                                    </a>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
}
